import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Routes, Route } from 'react-router-dom';
import Button from 'components/Button';
import SoldOutModal from 'components/modals/SoldOutModal';
import RESERVATIONS from 'constants/reservations';
import ANALYTICS from 'constants/analytics';
import { scrollLock } from 'utils/scrollLock';
import { analyticsInteraction, analyticsPrepareKey } from 'utils/analytics';
import { i18n } from 'utils/i18n';
import { isAtStartOfStandAloneBookingWidgetPage } from 'utils/dom';
import { isFreeUpgradeAvailable } from 'utils/vehicles';
import { withRouter, OnUpdateWrapper } from 'utils/hoc';
import { announceAssistiveStatus } from 'utils/accessibility/assistiveStatusAnnouncer';
import useClickOutside from 'hooks/useClickOutside';
import { ProgressBarTab } from './ProgressBarTab';
import { PROGRESS_BAR_CONFIG, MOBILE_PROGRESS_BAR_CONFIG } from '../reservationFlowRouteUtils';
import ProgressBarDrawerContainer from './ProgressBarDrawerContainer';
import ProgressBarHeaderContainer from './ProgressBarTab/ProgressBarHeaderIndicator';

const ORDERED_PATHS = Object.values(RESERVATIONS.RESFLOW_PATHS_CONFIG).flat();

const { EXCHANGE_TYPE_PICKUP, EXCHANGE_TYPE_RETURN, RESFLOW_PATHS_CONFIG } = RESERVATIONS;

const ProgressBar = (props) => {
  const {
    breakpoint,
    gmiReservationReady,
    haveInitiateRequest,
    isTrueModify,
    coupons,
    expandedTab,
    mobileExpanded,
    expandedTotalDropdown,
    router,
    dispatchExpandedTab,
    dispatchMobileExpanded,
    didProgressBarLocationInitiate,
    openModals,
    gmaPickupLocation,
    gmaReturnLocation,
    setLocation,
    initiateReservation,
    gboPickupLocation,
    gboReturnLocation,
    lastValidSearchedLocationId,
    adjustLocationId,
    dispatchSetAdjustLocation,
    clearVehiclesFilterData,
    setSelectedCurrencyToggleData,
    getVehiclesList,
    isFetchingSession,
  } = props;

  const [stickyBar, setStickyBar] = useState(false);
  const progressBarLayout = useRef(null);
  const clickOutsideRef = useRef(null);

  const handlePageScroll = () => {
    const { isMobile } = breakpoint;
    if (!isMobile) {
      const clientRect = progressBarLayout.current?.getBoundingClientRect();
      if (clientRect) {
        const { top } = clientRect;
        if (top <= 0 && !stickyBar) {
          setStickyBar(true);
        } else if (top > 0 && stickyBar) {
          setStickyBar(false);
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener('scroll', handlePageScroll);
    return () => {
      document.removeEventListener('scroll', handlePageScroll);
    };
  }, [stickyBar]); // to use the latest value of stickyBar in the handlePageScroll

  useEffect(() => {
    if (mobileExpanded) {
      scrollLock({ toggleValue: true });
    } else {
      scrollLock({ toggleValue: false });
    }
  }, [mobileExpanded]);

  // function to reset gmi>ui to match with gbo
  const resetPickuplocationToMatchWithGBO = () => {
    const locationSelectPage = RESFLOW_PATHS_CONFIG.location[0];
    const windowHash = window.location.hash.replace('#', '');

    // if location from GMA is different than the one in GBO
    // AND last valid location/pickup_location_id is different than pickup location from GBO, should update with GBO info
    // reset gmi > ui, to match what's in gbo
    // not call this if on location select page
    if (
      windowHash !== locationSelectPage &&
      !didProgressBarLocationInitiate &&
      ((gboPickupLocation?.name &&
        gmaPickupLocation?.name !== gboPickupLocation.name &&
        gmaPickupLocation?.id !== gboPickupLocation.id) ||
        (gboReturnLocation.name &&
          gmaReturnLocation?.name !== gboReturnLocation.name &&
          gmaReturnLocation?.id !== gboReturnLocation.id)) &&
      lastValidSearchedLocationId !== gboPickupLocation.id
    ) {
      setLocation(gboPickupLocation, EXCHANGE_TYPE_PICKUP);
      setLocation(gboReturnLocation, EXCHANGE_TYPE_RETURN);
    }
  };

  const handleExpandTab = (tabId) => {
    dispatchExpandedTab(tabId || null);

    if (!didProgressBarLocationInitiate) {
      resetPickuplocationToMatchWithGBO();
    }
    announceAssistiveStatus(''); // Screen reader announces this
  };

  const handleCollapseProgressBar = () => {
    if (mobileExpanded) {
      resetPickuplocationToMatchWithGBO();
      dispatchMobileExpanded(false);
    }
    if (expandedTab) {
      resetPickuplocationToMatchWithGBO();
      dispatchExpandedTab(null);
    }
  };

  const handleClickOutside = () => {
    const locationSelectPage = RESFLOW_PATHS_CONFIG.location[0];
    const windowHash = window.location.hash.replace('#', '');

    if (openModals.length === 0) {
      expandedTab && dispatchExpandedTab(null);
      mobileExpanded && dispatchMobileExpanded(false);
      scrollLock({ toggleValue: false });

      // If progress bar is expanded
      // AND if location from GMA is different than the one in GBO
      // AND last valid location/pickup_location_id is different than pickup location from GBO, should update with GBO info
      // reset gmi > ui, to match what's in gbo
      // not call this if on location select page
      if (
        windowHash !== locationSelectPage &&
        (!!expandedTab || mobileExpanded) &&
        ((gboPickupLocation?.name &&
          gmaPickupLocation?.name !== gboPickupLocation.name &&
          gmaPickupLocation?.id !== gboPickupLocation.id) ||
          (gboReturnLocation.name &&
            gmaReturnLocation?.name !== gboReturnLocation.name &&
            gmaReturnLocation?.id !== gboReturnLocation.id)) &&
        lastValidSearchedLocationId !== gboPickupLocation.id
      ) {
        setLocation(gboPickupLocation, EXCHANGE_TYPE_PICKUP);
        setLocation(gboReturnLocation, EXCHANGE_TYPE_RETURN).then(() => {
          initiateReservation();
        });
        clearVehiclesFilterData();
        setSelectedCurrencyToggleData();
        getVehiclesList(gboPickupLocation.address.country_code);
      }

      // If there's adjustLocationId it means that used gave up on changing Location Date Time for that sold out location
      if ((!!expandedTab || mobileExpanded) && adjustLocationId) {
        dispatchSetAdjustLocation(null);
      }
    }
  };

  useClickOutside(
    {
      container: clickOutsideRef,
      handler: handleClickOutside,
      listenerConditional: openModals,
    },
    [openModals]
  );

  const handleCloseDrawer = (responseText = '') => {
    expandedTab && dispatchExpandedTab(null);

    if (!didProgressBarLocationInitiate) {
      resetPickuplocationToMatchWithGBO();
    }
    if (expandedTab) {
      announceAssistiveStatus(responseText); // Screen reader announces this
    }
  };

  const handleBackClick = () => {
    expandedTab && dispatchExpandedTab(null);
    let key = '';
    if (expandedTab === 'datesTimes') {
      key = ANALYTICS.DATE_TIME;
    }
    if (expandedTab === 'location') {
      key = ANALYTICS.LOCATION_PAGE;
    }
    analyticsInteraction(analyticsPrepareKey(ANALYTICS.PROGRESS_BAR), key, ANALYTICS.BACK);
  };

  const toggleExpandMobile = () => {
    expandedTab && dispatchExpandedTab(null);
    dispatchMobileExpanded(!mobileExpanded);

    if (!didProgressBarLocationInitiate) {
      resetPickuplocationToMatchWithGBO();
    }

    analyticsInteraction(ANALYTICS.DRAWER, 'mobile progress bar', !mobileExpanded ? ANALYTICS.OPEN : ANALYTICS.CLOSE);
  };

  const handleRouteUpdates = () => handleCollapseProgressBar();

  const renderExpandedComponent = (path) => (
    <ProgressBarDrawerContainer
      onCloseDrawer={handleCloseDrawer}
      handleCollapseProgressBar={handleCollapseProgressBar}
      isFetchingReservation={isFetchingSession}
      currentPage={path}
      expandedTab={expandedTab}
      expandedTotalDropdown={expandedTotalDropdown}
    />
  );

  const getActiveProgressBarName = (match) => {
    const reservationFlowRoutes = MOBILE_PROGRESS_BAR_CONFIG.filter(
      (item) => item.paths.includes(match?.pathname) || item.disabledOnPaths?.includes(match?.pathname)
    );
    let progressTitle = i18n(reservationFlowRoutes[0]?.progressBarLabel);
    progressTitle =
      progressTitle === i18n('location_tiles_header_singular')
        ? 'location_tiles_header_plural'
        : reservationFlowRoutes[0]?.progressBarLabel;
    return progressTitle;
  };

  const { isMobile } = breakpoint;
  const shouldRenderTabs = gmiReservationReady && (!isAtStartOfStandAloneBookingWidgetPage || haveInitiateRequest);
  const freeUpgradeApplied = isFreeUpgradeAvailable(coupons);

  const match = router?.location;
  const location = router?.location;
  const locationPath = ORDERED_PATHS.find((path) => path === location?.pathname) || '';

  const renderProgressBarUI = () => (
    <>
      {!isMobile && (
        <>
          {stickyBar && <div className='progress-bar__sticky-layout-saver' ref={progressBarLayout} />}
          <div className={cn('progress-bar', { sticky: stickyBar })} ref={!stickyBar ? progressBarLayout : null}>
            <div className={cn('progress-bar__overlay', { visible: expandedTab })} />
            <div className='progress-bar__container' ref={clickOutsideRef}>
              <div className='progress-bar__tabs'>
                {shouldRenderTabs &&
                  PROGRESS_BAR_CONFIG.map((config) => (
                    <React.Fragment key={config.id}>
                      <ProgressBarTab
                        config={config}
                        expanded={config.id === expandedTab}
                        handleExpandTab={handleExpandTab}
                        key={config.id}
                        match={match}
                      />
                      {config.id === expandedTab && expandedTab && renderExpandedComponent(location?.pathname)}
                    </React.Fragment>
                  ))}
              </div>
            </div>
          </div>
        </>
      )}

      {isMobile && shouldRenderTabs && (
        <div
          className={cn('progress-bar-mobile', {
            'true-modify': isTrueModify,
            'total-dropdown-bar-height': expandedTotalDropdown,
          })}
          ref={clickOutsideRef}
        >
          {gmiReservationReady && (
            <>
              {!expandedTotalDropdown && (
                <div className='progress-bar-mobile__header-wrapper' onClick={toggleExpandMobile}>
                  <div className='mobile-progress-bar__header-row-text-conent'>
                    <Button
                      button={false}
                      className={cn('progress-bar-mobile__header-expand-button', {
                        expanded: mobileExpanded,
                      })}
                      ariaExpanded={mobileExpanded.toString()}
                      ariaText={i18n(
                        mobileExpanded ? 'progress_bar_collapse_accessibility' : 'progress_bar_expand_accessibility'
                      )}
                    >
                      <span className='mobile-progress-bar__button-label'>{i18n(getActiveProgressBarName(match))}</span>
                    </Button>
                  </div>
                  <ol className='mobile-progress-bar__header-row'>
                    {MOBILE_PROGRESS_BAR_CONFIG.map((config, index) => (
                      <ProgressBarHeaderContainer
                        key={index}
                        value={index + 1}
                        config={config}
                        match={match}
                        progressBarConfig={MOBILE_PROGRESS_BAR_CONFIG}
                      />
                    ))}
                  </ol>
                </div>
              )}
              {mobileExpanded && (
                <div className='progress-bar-mobile__expanded-drop-down-overlay'>
                  <div className='progress-bar-mobile__expanded-drop-down'>
                    {!expandedTab &&
                      MOBILE_PROGRESS_BAR_CONFIG.map((config, index) => (
                        <ProgressBarTab
                          config={config}
                          expanded={config.id === expandedTab}
                          handleExpandTab={handleExpandTab}
                          key={config.id}
                          match={match}
                          listNumber={index + 1}
                          coupons={index === 0 ? coupons : null}
                          mobile
                          freeUpgradeApplied={freeUpgradeApplied}
                        />
                      ))}
                    {expandedTab && (
                      <div className='progress-bar-mobile__expanded-tab'>
                        <Button link={true} className='expanded-tab__back-button' onClick={handleBackClick}>
                          {i18n('progress_bar_expanded_tab_back_button')}
                        </Button>
                        {renderExpandedComponent(location?.pathname)}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* handling totaldropdown expanded view ui */}
              {expandedTotalDropdown && (
                <div className='progress-bar-mobile__expanded-drop-down-overlay'>
                  <div className='progress-bar-mobile__expanded-drop-down'>
                    <div className='progress-bar-mobile__expanded-tab'>
                      {!expandedTotalDropdown && (
                        <Button link={true} className='expanded-tab__back-button' onClick={handleBackClick}>
                          {i18n('progress_bar_expanded_tab_back_button')}
                        </Button>
                      )}
                      {renderExpandedComponent(location.pathname)}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isMobile && <div className='progress-bar-mobile__layout-saver' />}
      <SoldOutModal
        currentPage={location?.pathname}
        resetPickuplocationToMatchWithGBO={resetPickuplocationToMatchWithGBO}
      />
    </>
  );

  return (
    <OnUpdateWrapper pathname={location?.pathname} onUpdate={handleRouteUpdates}>
      <Routes>
        <Route path={locationPath} element={renderProgressBarUI()} />
      </Routes>
    </OnUpdateWrapper>
  );
};

ProgressBar.propTypes = {
  // Parent props
  isTrueModify: PropTypes.bool,

  // Container Props
  breakpoint: PropTypes.object.isRequired,
  gmiReservationReady: PropTypes.bool,
  showReturnLocation: PropTypes.bool,
  haveOpenModal: PropTypes.array,
  coupons: PropTypes.array.isRequired,
  expandedTab: PropTypes.string,
  mobileExpanded: PropTypes.bool,
  gmaPickupLocation: PropTypes.object,
  gmaReturnLocation: PropTypes.object,
  gboPickupLocation: PropTypes.object,
  gboReturnLocation: PropTypes.object,
  isFetchingSession: PropTypes.bool,
  lastValidSearchedLocationId: PropTypes.string,
};

export default withRouter(ProgressBar);
